import { useState, useCallback, memo } from 'react';
import './App.css';

// Extracted ProjectCard component for better performance
const ProjectCard = memo(({ project, onShowMore }) => {
  return (
    <div className="col-lg-6 col-sm-12 pt-4 pt-lg-0 mb-5 content resume" data-aos="fade-left">
      <div className="resume-item pb-0 project-content">
        <h4>
          <a href={project.notLive ? "javascript:;" : project.url} target={project.notLive ? undefined : "_blank"} rel="noreferrer">
            {project.title} {!project.notLive && project.url !== "#" && <i className="mdi mdi-open-in-new ms-2" />}
          </a>
          {project.notLive && <span className="badge bg-danger ms-2" style={{ fontSize: "0.6em", verticalAlign: "middle" }}>Not Live</span>}
        </h4>
        <p><em>{project.tech}</em></p>

        {/* Project Image Carousel */}
        <div id={`${project.id}Carousel`} className="carousel slide mb-4" data-bs-ride="carousel">
          <div className="carousel-indicators">
            {project.images.map((_, index) => (
              <button
                key={index}
                type="button"
                data-bs-target={`#${project.id}Carousel`}
                data-bs-slide-to={index}
                className={index === 0 ? "active" : ""}
              ></button>
            ))}
          </div>
          <div className="carousel-inner">
            {project.images.map((img, index) => (
              <div key={index} className={`carousel-item ${index === 0 ? "active" : ""}`}>
                <img src={img} className="d-block w-100 project-image" alt={`${project.title}`} loading="lazy" />
              </div>
            ))}
          </div>
          <button className="carousel-control-prev" type="button" data-bs-target={`#${project.id}Carousel`} data-bs-slide="prev">
            <span className="carousel-control-prev-icon" aria-hidden="true"></span>
            <span className="visually-hidden">Previous</span>
          </button>
          <button className="carousel-control-next" type="button" data-bs-target={`#${project.id}Carousel`} data-bs-slide="next">
            <span className="carousel-control-next-icon" aria-hidden="true"></span>
            <span className="visually-hidden">Next</span>
          </button>
        </div>

        {/* Short Description with Show More Button */}
        <div className="project-description show-more-btn">
          <p className='mb-2 text-justify'>{project.shortDescription}</p>
          {project.impact && (
            <p className='mb-2 text-justify'><strong>Impact:</strong> {project.impact}</p>
          )}
          <button
            className="btn btn-primary"
            onClick={() => onShowMore(project.id)}
            aria-label={`Show more details about ${project.title}`}
          >
            Show More
          </button>
        </div>
      </div>
    </div>
  );
});

// Extracted ProjectModal component
const ProjectModal = memo(({ project, isActive, onClose }) => {
  if (!project) return null;

  return (
    <div
      className={`modal fade ${isActive ? 'show' : ''}`}
      tabIndex="-1"
      aria-labelledby={`${project.id}ModalLabel`}
      aria-hidden={!isActive}
      style={{ display: isActive ? 'block' : 'none' }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id={`${project.id}ModalLabel`}>
              {project.title}
              {project.notLive && <span className="badge bg-danger ms-2" style={{ fontSize: "0.6em", verticalAlign: "middle" }}>Not Live</span>}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
          </div>
          <div className="modal-body">
            <div className="tech-stack mb-4">
              <span className="badge bg-secondary me-2">Tech Stack</span>
              {project.tech.split(', ').map((tech, index) => (
                <span key={index} className="badge bg-primary me-1 mb-1">{tech}</span>
              ))}
            </div>

            {/* Modal Carousel with More Images */}
            <div id={`${project.id}ModalCarousel`} className="carousel slide mb-4" data-bs-ride="carousel">
              <div className="carousel-indicators">
                {project.images.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    data-bs-target={`#${project.id}ModalCarousel`}
                    data-bs-slide-to={index}
                    className={index === 0 ? "active" : ""}
                  ></button>
                ))}
              </div>
              <div className="carousel-inner">
                {project.images.map((img, index) => (
                  <div key={index} className={`carousel-item ${index === 0 ? "active" : ""}`}>
                    <img src={img} className="d-block w-100 project-image-modal" alt={`${project.title}`} loading="lazy" />
                  </div>
                ))}
              </div>
              <button className="carousel-control-prev" type="button" data-bs-target={`#${project.id}ModalCarousel`} data-bs-slide="prev">
                <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                <span className="visually-hidden">Previous</span>
              </button>
              <button className="carousel-control-next" type="button" data-bs-target={`#${project.id}ModalCarousel`} data-bs-slide="next">
                <span className="carousel-control-next-icon" aria-hidden="true"></span>
                <span className="visually-hidden">Next</span>
              </button>
            </div>

            {/* Full Description */}
            <div className="project-full-description">
              <h6 className="fw-bold section-title">Project Overview</h6>
              {project.fullDescription.map((paragraph, index) => (
                <p key={index} className="text-justify mb-2">{paragraph}</p>
              ))}
            </div>

            {/* Impact Statement */}
            {project.impact && (
              <div className="project-impact mt-4">
                <h6 className="fw-bold section-title">Impact</h6>
                <p className="text-justify mb-2">{project.impact}</p>
              </div>
            )}

            {/* Responsibilities if available */}
            {project.responsibilities && (
              <div className="project-responsibilities mt-4">
                <h6 className="fw-bold section-title">My Responsibilities</h6>
                <ul className="mb-0 ps-0">
                  {project.responsibilities.map((responsibility, index) => (
                    <li key={index}>{responsibility}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Integrations if available */}
            {project.integrations && (
              <div className="project-integrations mt-4">
                <h6 className="fw-bold section-title">Third-Party Integrations</h6>
                <ul className="mb-0 ps-0">
                  {project.integrations.map((integration, index) => (
                    <li key={index}>
                      <b>{integration.name}</b> {integration.desc}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="modal-footer">
            {!project.notLive && project.url !== "#" && (
              <a href={project.url} target="_blank" rel="noreferrer" className="btn btn-primary">
                Visit Website <i className="mdi mdi-open-in-new ms-1"></i>
              </a>
            )}
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
});

// Project data - ordered per resume: Hommies, BitCash, Solos, Mica, Bit Token, then extras
const projectsData = [
  {
    id: "hommies",
    title: "Hommies",
    url: "https://hommies.io/",
    notLive: true,
    tech: "ReactJS, NextJS, NodeJS, MongoDB, PubNub, TradingView, Pinata, Thirdweb",
    impact: "Enabled real-time trading visualization and secure wallet integrations.",
    shortDescription:
      "Hommies is a crypto platform built around the HOMMIES Token. Initially focused on crypto swapping (like BTC to HOMMIES), it expands to offer staking, marketplace access, and decentralized document storage, aiming to create a multi-functional crypto ecosystem with seamless user experiences.",
    fullDescription: [
      "The platform introduces the HOMMIES Token, initially focusing on crypto swapping like BTC to HOMMIES. Phase two expands to include staking, a marketplace, document storage, and more.",
      "Key features include real-time crypto price tracking, wallet balances with conversion rates, profile status management, and transaction handling. Built using React.js and Next.js on the frontend, and Node.js with MongoDB on the backend.",
      "Gained experience with smart contract integration, staking mechanisms, fiat-to-crypto conversions, transaction tracking, and secure document storage."
    ],
    responsibilities: [
      "Collaborated with the senior team lead on system architecture and database design.",
      "Developed and optimized core backend logic, emphasizing performance, security, and scalability.",
      "Led a team and managed task delegation for both frontend and backend development while also contributing hands-on.",
      "Handled deployment with a focus on performance, security, and uptime."
    ],
    integrations: [
      { name: "TradingView", desc: "Integrated live trading charts, watchlists, and tickers." },
      { name: "Pinata", desc: "Implemented decentralized document storage." },
      { name: "1inch", desc: "Fetched wallet balances and transaction data." },
      { name: "Thirdweb", desc: "Handled wallet connections and blockchain transactions." }
    ],
    images: [
      "/assets/img/projects/Hommies1.PNG",
      "/assets/img/projects/Hommies2.PNG",
      "/assets/img/projects/Hommies3.PNG",
      "/assets/img/projects/Hommies4.PNG"
    ]
  },
  {
    id: "bittoken",
    title: "Bit Token",
    url: "https://bittokenapp.com",
    tech: "ReactJS, NodeJS, MongoDB, Redis, RabbitMQ",
    impact: "Bittoken is a crypto reward and staking platform that allows users to earn and stake reward points through mining, referrals, and admin-driven tasks. Users can convert reward points to BTT and stake them to earn rewards to own wallet.",
    shortDescription:
      "",
    fullDescription: [
      "Developed a blockchain-integrated platform where users earn and stake reward points through mining, referrals, and admin-driven tasks.",
      "Enabled seamless conversion between reward points, BTT, and USDT with secure wallet operations for deposits and withdrawals."
    ],
    responsibilities: [
      "Architected and developed the backend system for high-concurrency reward distribution.",
      "Implemented secure wallet operations for deposits, withdrawals, and conversions.",
      "Optimized API performance to handle 25K-30K concurrent requests efficiently.",
    ],
    integrations: [
      { name: "RabbitMQ", desc: "Message queuing for high-volume concurrent requests." },
      { name: "Redis", desc: "Caching layer for performance optimization." }
    ],
    images: [
      "/assets/img/projects/BITTOKEN1.PNG",
      "/assets/img/projects/BITTOKEN2.PNG",
      "/assets/img/projects/BITTOKEN3.PNG",
      "/assets/img/projects/BITTOKEN4.PNG",
      "/assets/img/projects/BITTOKEN5.PNG"
    ]
  },
  {
    id: "bitcashex",
    title: "BitCash Exchange",
    url: "https://bitcashex.com/",
    notLive: true,
    tech: "ReactJS, NodeJS, MongoDB, Redis, RabbitMQ, Binance API",
    impact: "Boosted transaction efficiency by 50%.",
    shortDescription:
      "BitCash Exchange is a complete crypto trading platform offering fiat-to-crypto services, staking rewards, referral bonuses, and multiple trading features such as spot, margin, and futures trading. It is designed for high performance, user security, and advanced trading experiences through real-time insights and analytics.",
    fullDescription: [
      "BitCash Exchange allows users to purchase and sell crypto using fiat, with additional features like staking, referral earnings, and multi-mode trading (spot, margin, and futures).",
      "The platform is designed to provide a secure, high-performance trading experience with advanced analytics and tools."
    ],
    responsibilities: [
      "Developed P2P, limit order, and futures trading functionality.",
      "Ensured secure operations via encryption, IP/domain restrictions, and throttling.",
      "Integrated third-party APIs and managed complete backend development."
    ],
    integrations: [
      { name: "Binance", desc: "Used for real-time charting and trading insights." },
      { name: "RabbitMQ", desc: "Handled high-volume request processing." },
      { name: "Redis", desc: "Enhanced system performance through caching." }
    ],
    images: [
      "/assets/img/projects/BITCASH1.PNG",
      "/assets/img/projects/BITCASH2.PNG",
      "/assets/img/projects/BITCASH3.PNG",
      "/assets/img/projects/BITCASH4.PNG",
      "/assets/img/projects/BITCASH5.PNG"
    ]
  },
  {
    id: "solos",
    title: "Solos",
    url: "https://solosfi.com/",
    tech: "ReactJS, NodeJS, MongoDB, Redis",
    impact: "Increased transaction success rate by 30%.",
    shortDescription:
      "Solos is a peer-to-peer crypto marketplace that allows users to buy Red Tokens using fiat currency and trade them securely. The platform features include KYC verification, real-time chat, secure wallet transactions, and USD-to-crypto conversions, offering a smooth and compliant trading environment.",
    fullDescription: [
      "Solos is a peer-to-peer marketplace supporting Red Token, FRed Token, and Super FRed. Users can purchase Red Tokens with fiat currency and trade within the platform.",
      "The platform includes KYC verification, real-time chat support, and USD-to-crypto conversions. The frontend uses React.js and Next.js, and the backend is built with Node.js and MongoDB.",
      "Managed both backend development and team coordination to ensure seamless, secure live deployment."
    ],
    integrations: [
      { name: "FaceTec", desc: "Provided KYC document verification and facial recognition." },
      { name: "Mirrorfly", desc: "Enabled real-time chat support for users." },
      { name: "Bridge", desc: "Handled fiat to USDT conversion securely." },
      { name: "Thirdweb", desc: "Integrated wallet connectivity and blockchain interaction." }
    ],
    images: [
      "/assets/img/projects/SOLO1.PNG",
      "/assets/img/projects/SOLO2.PNG",
      "/assets/img/projects/SOLO3.PNG",
      "/assets/img/projects/SOLO4.PNG"
    ]
  },
  {
    id: "micawallet",
    title: "Mica Wallet (Web Extension)",
    url: "#",
    tech: "NodeJS, ReactJS, MongoDB, FixParser, CoinMarketCap API, Ethers.js",
    shortDescription:
      "Mica Wallet is a crypto browser extension that functions similarly to MetaMask, enabling users to manage their crypto assets, perform transactions, and seamlessly interact with decentralized applications (dApps) directly from any website with high-level security and encryption mechanisms in place.",
    fullDescription: [
      "Mica Wallet is a smart crypto management tool, available as a web extension. It allows users to send/receive crypto on any website and connect to decentralized apps (dApps).",
      "The project focuses on secure, seamless wallet interaction and transaction execution through the browser extension."
    ],
    responsibilities: [
      "Collaborated on system design and data architecture with the senior lead.",
      "Implemented robust security, including encryption, IP/domain restrictions, and rate limiting.",
      "Independently developed the extension and backend logic.",
      "Project is currently in progress and nearing completion."
    ],
    images: [
      "/assets/img/projects/MICA1.PNG",
      "/assets/img/projects/MICA2.PNG",
      "/assets/img/projects/MICA3.PNG",
      "/assets/img/projects/MICA4.PNG",
      "/assets/img/projects/MICA5.PNG",
      "/assets/img/projects/MICA6.PNG",
      "/assets/img/projects/MICA7.PNG",
      "/assets/img/projects/MICA8.PNG",
      "/assets/img/projects/MICA9.PNG"
    ]
  },
  {
    id: "amexaexchange",
    title: "Amexa Exchange",
    url: "https://amexa.exchange/",
    tech: "ReactJS, NodeJS, Express, MongoDB",
    shortDescription:
      "Amexa Exchange is a powerful crypto trading platform that facilitates fiat-to-crypto transactions. It offers users multiple trading options including spot, margin, and futures trading, along with staking, referral bonuses, and a secure trading environment designed for both beginners and professionals seeking efficient and scalable financial tools.",
    fullDescription: [
      "Amexa Exchange enables users to buy/sell crypto using fiat currency. Features include staking, referral rewards, and advanced trading (spot, margin, futures).",
      "Users can also create trading accounts and access financial tools within a secure and scalable environment."
    ],
    responsibilities: [
      "Built systems for P2P, limit, and futures trading.",
      "Implemented encryption, IP/domain restrictions, and performance rate-limiting.",
      "Handled third-party integrations and complete development lifecycle."
    ],
    integrations: [
      { name: "Binance", desc: "Integrated live charts with advanced technical indicators." },
      { name: "RabbitMQ", desc: "Managed concurrent request handling and job queues." },
      { name: "Redis", desc: "Implemented caching for improved performance." }
    ],
    images: [
      "/assets/img/projects/AMEXA1.PNG",
      "/assets/img/projects/AMEXA2.PNG",
      "/assets/img/projects/AMEXA3.PNG",
      "/assets/img/projects/AMEXA4.PNG"
    ]
  },
  {
    id: "universeye",
    title: "The Universe Eye",
    url: "https://theuniverseye.com/",
    tech: "ReactJS, NextJS, NodeJS, Express, MongoDB",
    shortDescription:
      "The Universe Eye is an immersive metaverse experience of Dubai's Bluewaters Island. Users can explore virtual properties, buy NFTs, or interact with 3D environments through Unity. It includes a backend-driven admin panel for managing NFTs, collections, and multi-platform integrations.",
    fullDescription: [
      "The Universe Eye offers a virtual tour of Dubai's Bluewaters Island, allowing users to purchase properties via NFTs or explore interactively in Unity.",
      "I developed the admin panel to manage properties, categories, NFTs, and collections. Also created backend APIs for seamless integration across web, mobile, and Unity platforms."
    ],
    responsibilities: [
      "Worked with the lead architect on database and platform design.",
      "Built and maintained the admin panel and backend APIs.",
      "Ensured secure, optimized live deployment for global accessibility."
    ],
    images: [
      "/assets/img/projects/UNIVERSE1.PNG",
      "/assets/img/projects/UNIVERSE2.PNG",
      "/assets/img/projects/UNIVERSE3.PNG"
    ]
  }
];

function Project({ section, setSection }) {
  const [activeModal, setActiveModal] = useState(null);

  const openModal = useCallback((projectId) => {
    document.body.classList.add('modal-open');
    setActiveModal(projectId);
  }, []);

  const closeModal = useCallback(() => {
    document.body.classList.remove('modal-open');
    setActiveModal(null);
  }, []);

  const activeProject = activeModal ? projectsData.find(project => project.id === activeModal) : null;

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && activeModal) {
      closeModal();
    }
  }, [activeModal, closeModal]);

  useState(() => {
    if (activeModal) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeModal, handleKeyDown]);

  return (
    <>
      {/* ======= Projects Section ======= */}
      <section id="projects" className={`services ${section === "projects" ? "section-show" : ""}`}>
        <div className="container">
          <div className="section-title">
            <h2>Projects</h2>
            <p>Featured Projects</p>
          </div>
          <div className="row">
            {projectsData.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onShowMore={openModal}
              />
            ))}
          </div>
        </div>
      </section>{/* End Projects Section */}

      {/* Active Project Modal */}
      {activeProject && (
        <ProjectModal
          project={activeProject}
          isActive={!!activeModal}
          onClose={closeModal}
        />
      )}

      {/* Modal Backdrop */}
      {activeModal && (
        <div className="modal-backdrop fade show" onClick={closeModal}></div>
      )}
    </>
  );
}

export default Project;
